import { Link } from 'react-router-dom'
import { Button, Price } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { capability, consultingBundle } from '@gjl/voice/pricing'

// This page is a slim hub: the systems it used to describe now have dedicated
// solution pages. It stays alive so existing links and search results resolve.

const email = capability('email')!
const donorMgmt = capability('donor-management')!
const ticketing = capability('ticketing')!
const intensive = consultingBundle('strategy-intensive')

const SYSTEMS = [
  {
    title: 'Email Marketing',
    href: '/services/email-marketing',
    price: `$${email.priceMonthly}/mo`,
    description: 'Newsletters, sequences, and season campaigns. Self-serve today, or fully managed.',
  },
  {
    title: 'Donor Management',
    href: '/services/donor-management',
    price: `$${donorMgmt.priceMonthly}/mo`,
    description: 'Accepting donations is free (0% platform fee). This is the layer that grows giving.',
  },
  {
    title: 'Ticketing',
    href: '/services/ticketing',
    price: `$${ticketing.priceMonthly}/mo + ${ticketing.perUnitFee}`,
    description: 'Sell on your own site with QR check-in and reporting, not on a marketplace.',
  },
]

export default function DigitalSystemsPage() {
  const breadcrumbs = generateBreadcrumbs('/services/digital-systems', breadcrumbLabels)

  useSEO({
    title: 'Digital Systems for Music Organizations',
    description:
      'Email marketing, donor management, and ticketing on one system with one audience record. Each capability has its own page and price.',
    url: '/services/digital-systems',
    canonical: '/services/digital-systems',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Digital Systems for Music Organizations',
      description: 'Email marketing, donor management, and ticketing for music organizations, unified on one system.',
      url: '/services/digital-systems',
    }),
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-20 md:space-y-36 md:px-12 md:py-28">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">The systems behind the season</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Email, giving, and ticketing each have their own page now, because each is its own decision. What they share
          is the part that matters: one system, one audience record.
        </p>
      </section>

      {/* The three systems, as a typographic list of links (the home page's
          pattern) rather than three icon cards with an outline button each.
          The whole row is the link; hover changes the name's colour only. */}
      <section aria-label="Systems">
        <ul className="space-y-2">
          {SYSTEMS.map((sys) => (
            <li key={sys.href}>
              <Link
                to={sys.href}
                className="group focus-visible:outline-primary grid gap-3 py-6 focus-visible:outline-2 focus-visible:outline-offset-4 md:grid-cols-12 md:items-baseline md:gap-8 md:py-8"
              >
                <div className="space-y-2 md:col-span-7">
                  <h2 className="group-hover:text-primary text-[clamp(2rem,4.5vw,3.75rem)] leading-[1.05] font-normal transition-colors duration-200">
                    {sys.title}
                  </h2>
                  <Price>{sys.price}</Price>
                </div>
                <span className="text-muted-foreground max-w-[48ch] leading-relaxed md:col-span-5">
                  {sys.description}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Unified record + consulting. Was a tinted bordered panel; now an
          asymmetric 5/7 split on the page ground. */}
      <section aria-labelledby="one-system" className="grid gap-6 md:grid-cols-12 md:gap-8">
        <h2 id="one-system" className="text-fluid-h3 md:col-span-5">
          Why one system beats three tools
        </h2>
        <div className="space-y-5 md:col-span-7">
          <p className="text-muted-foreground text-fluid-body max-w-[60ch] leading-relaxed">
            Your subscribers, donors, and ticket buyers are the same people. When the systems share one audience record,
            every campaign can see giving history, every appeal can see attendance, and nothing drifts between
            spreadsheets.
          </p>
          <p className="text-muted-foreground max-w-[60ch] text-sm leading-relaxed">
            Not sure what your organization needs first? The {intensive.name} ({intensive.price}) is an audit plus
            roadmap plus setup, and the plan is yours either way. Details under{' '}
            <Link
              to="/pricing#consulting"
              className="text-primary focus-visible:outline-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              consulting
            </Link>
            .
          </p>
          <div className="pt-4">
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="cursor-pointer">
                Book a Free Call →
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
